<template>
  <!--
    PoolaOdometer — a fixed-slot rolling number for the festival live surfaces.
    The displayed `text` (a pre-formatted string, e.g. a currency amount) sits in
    a keyed span; every time `text` changes the key changes, Vue mounts a fresh
    span, and the CSS roll animation plays once on that fresh element — so the
    number "rolls up" into place on each real change with no JS timers.

    Honesty note: this component only DISPLAYS whatever `text` its parent passes;
    it never invents or tweens toward a value. The keyed node keys on `text`
    itself, so two identical formatted strings correctly produce no roll. `value`
    is the raw number, kept as a stable change signal / a hook for future
    direction-aware rolls — it is not otherwise consumed here.

    Motion: transform + opacity only (see the keyframes). Fully gated behind
    prefers-reduced-motion — `data-motion="reduced"` (JS) drops the animation, and
    a reduced-motion @media query is the belt-and-suspenders CSS fallback.
  -->
  <span class="poola-odometer" :data-motion="motion" :aria-label="text">
    <span :key="text" class="poola-odometer__value" aria-hidden="true">{{ text }}</span>
  </span>
</template>

<script setup>
import { ref } from 'vue';
import { prefersReducedMotion } from '@/utils/confetti.js';

defineProps({
  // The pre-formatted display string (parent owns formatting — SSOT useCurrency).
  text: { type: String, required: true },
  // Raw numeric value (reserved for future direction-aware rolls / parent change
  // detection); not otherwise consumed here.
  value: { type: Number, default: null },
});

// Read once per instance — the festival surfaces are short-lived, and reading it
// statically keeps the reduced-motion branch trivially testable (mock matchMedia
// before mount). CSS @media is the runtime safety net if the setting flips.
const motion = ref(prefersReducedMotion() ? 'reduced' : 'roll');
</script>

<style scoped>
.poola-odometer {
  display: inline-flex;
  overflow: hidden;
  vertical-align: bottom;
  font-variant-numeric: tabular-nums;
  line-height: 1.15;
}

.poola-odometer[data-motion='roll'] .poola-odometer__value {
  animation: poola-odometer-roll 0.42s cubic-bezier(0.2, 0.7, 0.3, 1);
}

@keyframes poola-odometer-roll {
  from {
    transform: translateY(0.85em);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .poola-odometer .poola-odometer__value {
    animation: none;
  }
}
</style>
